import React, { useState, useEffect, useContext } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
} from "@mui/material"

import {
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
  PictureAsPdf as PictureAsPdfIcon,
  DirectionsCar as DirectionsCarIcon,
  PersonPinCircle as PersonPinCircleIcon,
  MonetizationOn as MonetizationOnIcon,
} from "@mui/icons-material"
import Swal from "sweetalert2"
import dayjs from "dayjs"

// Importaciones SIAC
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { api } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"

// Exportación a PDF
import JsPDF from "jspdf"
import autoTable from "jspdf-autotable"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  padding: "0 16px",
  minHeight: "80vh",
}))

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const formatMoneda = (valor) => {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(valor || 0)
}

const HojaRutaCobroDetalle = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const facturaState = location.state
  const globalContext = useContext(GlobalContext)

  const [isLoading, setIsLoading] = useState(false)
  const [dataCobro, setDataCobro] = useState(null)

  useEffect(() => {
    if (!facturaState || !facturaState.facnumfac) {
      navigate(-1)
      return
    }
    cargarHojaRuta()
  }, [])

  const cargarHojaRuta = async () => {
    setIsLoading(true)
    try {
      const response = await api.post("/ConsDatosparaRecaudar/getHojaRutaCobro", {
        facnumfac: facturaState.facnumfac,
      })

      // SOLUCIÓN DEFINITIVA: Extractor Recursivo a prueba de Decoradores
      const findCobroData = (obj) => {
        if (!obj) return null
        if (obj.cabecera) return obj
        if (obj.data) return findCobroData(obj.data)
        return null
      }

      const payload = findCobroData(response)

      if (payload && payload.cabecera) {
        setDataCobro(payload)
      } else {
        Swal.fire("Aviso", "No se encontró la información de la hoja de ruta.", "warning")
        navigate(-1)
      }
    } catch (error) {
      console.error(error)
      Swal.fire("Error", "Ocurrió un problema al cargar los datos de recaudación.", "error")
    } finally {
      setIsLoading(false)
    }
  }

  // ==========================================================================
  // GENERACIÓN DEL REPORTE PDF DE LA HOJA DE RUTA
  // ==========================================================================
  const handleExportarPDF = () => {
    if (!dataCobro) return

    const doc = new JsPDF("p", "pt", "a4")
    const nombreCia = localStorage.getItem("ciaalias") || globalContext?.ciaSeleccionada?.label || "EMPRESA NO DEFINIDA"
    const usuarioSesion = localStorage.getItem("cliciausu") || globalContext?.user || "USUARIO DESCONOCIDO"

    // --- ENCABEZADO INSTITUCIONAL ---
    doc.setFontSize(16)
    doc.setTextColor(25, 108, 135)
    doc.text(nombreCia, 40, 40)

    doc.setFontSize(14)
    doc.setTextColor(0, 0, 0)
    doc.text("HOJA DE RUTA - GESTIÓN DE TERRENO", 40, 65)

    doc.setFontSize(10)
    doc.setTextColor(60, 60, 60)
    // doc.text(`Recaudador: ${usuarioSesion}`, 40, 85)
    doc.text(`Fecha de Impresión: ${dayjs().format("DD/MM/YYYY HH:mm")}`, 40, 100)

    // --- SECCIÓN 1: DATOS DE LA FACTURA ---
    doc.setDrawColor(25, 108, 135)
    doc.setLineWidth(1)
    doc.line(40, 115, 555, 115)

    doc.setFontSize(12)
    doc.setTextColor(25, 108, 135)
    doc.text("1. REFERENCIA DEL CRÉDITO", 40, 135)

    doc.setFontSize(10)
    doc.setTextColor(40, 40, 40)
    doc.text(`Documento (Factura): ${dataCobro.cabecera.facnumfac}`, 40, 155)
    doc.text(`Identificación Cliente: ${dataCobro.cabecera.cliruc}`, 40, 170)
    doc.text(`Nombre Cliente: ${dataCobro.cabecera.clinombre}`, 40, 185)

    // --- SECCIÓN 2: DATOS DEL CLIENTE Y GARANTE (CON FOTOS) ---
    doc.line(40, 200, 555, 200)
    doc.setFontSize(12)
    doc.setTextColor(25, 108, 135)
    doc.text("2. LOCALIZACIÓN Y FOTOS", 40, 220)

    doc.setFontSize(10)
    doc.setTextColor(40, 40, 40)
    doc.text("Dirección Cliente:", 40, 240)

    let currentY = 255
    if (dataCobro.cliente) {
      const domCli = doc.splitTextToSize(dataCobro.cliente.clidirec || "No registrada", 350)
      doc.text(domCli, 40, currentY)

      if (dataCobro.cliente.cliimagen_b64) {
        try {
          doc.addImage(dataCobro.cliente.cliimagen_b64, "JPEG", 420, 230, 80, 80)
        } catch (e) {}
      }
      currentY += domCli.length * 12 + 20
    }

    if (dataCobro.garante) {
      currentY = Math.max(currentY, 320)
      doc.text(`Garante: ${dataCobro.cabecera.garcodigo || "S/N"}`, 40, currentY)
      currentY += 15
      doc.text("Dirección Garante:", 40, currentY)
      currentY += 15
      const domGar = doc.splitTextToSize(dataCobro.garante.clidirec || "No registrada", 350)
      doc.text(domGar, 40, currentY)

      if (dataCobro.garante.cliimagen_b64) {
        try {
          doc.addImage(dataCobro.garante.cliimagen_b64, "JPEG", 420, currentY - 30, 80, 80)
        } catch (e) {}
      }
      currentY += domGar.length * 12 + 20
    }

    // --- SECCIÓN 3: BIEN PRENDADO (VEHÍCULOS) ---
    currentY = Math.max(currentY, 410)
    doc.line(40, currentY, 555, currentY)
    currentY += 20
    doc.setFontSize(12)
    doc.setTextColor(25, 108, 135)
    doc.text("3. DETALLE DEL BIEN A RECUPERAR", 40, currentY)
    currentY += 20

    doc.setFontSize(9)
    doc.setTextColor(40, 40, 40)
    if (dataCobro.vehiculos && dataCobro.vehiculos.length > 0) {
      dataCobro.vehiculos.forEach((v) => {
        const txtVeh = doc.splitTextToSize(`• ${v.descripcion_completa}`, 500)
        doc.text(txtVeh, 40, currentY)
        currentY += txtVeh.length * 12 + 10
      })
    } else {
      doc.text("Sin bienes serializados registrados.", 40, currentY)
      currentY += 20
    }

    // --- SECCIÓN 4: TABLA DE CUOTAS Y MORA ---
    currentY += 10
    doc.line(40, currentY, 555, currentY)
    currentY += 20
    doc.setFontSize(12)
    doc.setTextColor(25, 108, 135)
    doc.text("4. PLANILLA DE COBRO", 40, currentY)

    const tableColumn = ["Cuota", "F. Vence", "Días Mora", "Deuda Cap.", "Int. Mora", "TOTAL PAGO"]
    const tableRows = []

    let tCapital = 0
    let tMora = 0
    let tPagar = 0

    if (dataCobro.cuotas) {
      dataCobro.cuotas.forEach((c) => {
        tCapital += c.SaldoNeto || 0
        tMora += c.Interes || 0
        tPagar += c.ValorACobrar || 0

        tableRows.push([
          c.facsecuen,
          c.facfecven,
          c.lDiasVen || 0,
          `$${Number(c.SaldoNeto || 0).toFixed(2)}`,
          `$${Number(c.Interes || 0).toFixed(2)}`,
          `$${Number(c.ValorACobrar || 0).toFixed(2)}`,
        ])
      })
    }

    autoTable(doc, {
      startY: currentY + 15,
      head: [tableColumn],
      body: tableRows,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 4, textColor: [40, 40, 40] },
      headStyles: { fillColor: [25, 108, 135], textColor: [255, 255, 255], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      columnStyles: {
        0: { halign: "center" },
        1: { halign: "center" },
        2: { halign: "center" },
        3: { halign: "right" },
        4: { halign: "right", textColor: [211, 47, 47] },
        5: { halign: "right", fontStyle: "bold" },
      },
      didDrawPage: function (data) {
        const pageSize = doc.internal.pageSize
        const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight()
        const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth()
        doc.setFontSize(8)
        doc.setTextColor(150, 150, 150)
        doc.text(`Generado por: ${usuarioSesion}`, data.settings.margin.left, pageHeight - 20)
        doc.text(`Página ${doc.internal.getNumberOfPages()}`, pageWidth - data.settings.margin.right, pageHeight - 20, {
          align: "right",
        })
      },
    })

    // Totales Finales
    const finalY = doc.lastAutoTable.finalY + 20
    doc.setFontSize(12)
    doc.setTextColor(0, 0, 0)
    doc.text(`Capital Vencido: ${formatMoneda(tCapital)}`, 350, finalY, { align: "left" })
    doc.setTextColor(211, 47, 47)
    doc.text(`Total Interés Mora: ${formatMoneda(tMora)}`, 350, finalY + 15, { align: "left" })
    doc.setFontSize(14)
    doc.setTextColor(25, 108, 135)
    doc.text(`GRAN TOTAL A COBRAR: ${formatMoneda(tPagar)}`, 350, finalY + 35, { align: "left" })

    // Zona de Firmas
    doc.setDrawColor(0, 0, 0)
    doc.setLineWidth(1)
    doc.line(40, finalY + 80, 200, finalY + 80)
    doc.setFontSize(10)
    doc.setTextColor(0, 0, 0)
    doc.text("Firma del Cliente / Receptor", 60, finalY + 95)

    doc.save(`Hoja_Ruta_${dataCobro.cabecera.facnumfac}_${dayjs().format("YYYYMMDD")}.pdf`)
  }

  if (!dataCobro) {
    return (
      <ThemeProvider theme={theme}>
        <Header />
        <CustomBackdrop isLoading={isLoading} />
      </ThemeProvider>
    )
  }

  // Totales de la UI
  const granCapital = dataCobro.cuotas?.reduce((a, b) => a + (b.SaldoNeto || 0), 0) || 0
  const granMora = dataCobro.cuotas?.reduce((a, b) => a + (b.Interes || 0), 0) || 0
  const granTotal = granCapital + granMora

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <CustomBackdrop isLoading={isLoading} />

      <div className="main main-app p-2 p-lg-3" style={{ backgroundColor: "#f4f6f8", minHeight: "100vh" }}>
        {/* Cabecera Flotante */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          p={2}
          bgcolor="white"
          borderRadius={2}
          boxShadow={1}
        >
          <Box display="flex" alignItems="center">
            <BackIcon />
            <Typography variant="h5" fontWeight="bold" color="primary.main" ml={2}>
              Visor de Hoja de Ruta: {dataCobro.cabecera.facnumfac}
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="error"
              size="large"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleExportarPDF}
              sx={{ fontWeight: "bold" }}
            >
              Exportar Hoja de Ruta PDF
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ fontWeight: "bold" }}
            >
              Volver
            </Button>
          </Box>
        </Box>

        <StyledRoot>
          <Grid container spacing={3}>
            {/* LADO IZQUIERDO: ROSTROS Y LOCALIZACIÓN */}
            <Grid item xs={12} md={4}>
              {/* Tarjeta Cliente */}
              <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2, borderTop: "4px solid #196C87" }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <PersonPinCircleIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    Deudor Principal
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
                  <Avatar
                    src={
                      dataCobro.cliente?.cliimagen_b64
                        ? `data:image/jpeg;base64,${dataCobro.cliente.cliimagen_b64}`
                        : ""
                    }
                    sx={{ width: 120, height: 120, mb: 1, border: "2px solid #196C87" }}
                  />
                  <Typography variant="body1" fontWeight="bold" align="center">
                    {dataCobro.cabecera.clinombre}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {dataCobro.cabecera.cliruc}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="caption" color="textSecondary" fontWeight="bold">
                  Dirección Residencial / Visita
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {dataCobro.cliente?.clidirec || "Dirección no registrada"}
                </Typography>
                {dataCobro.cliente?.clidirec2 && (
                  <>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      fontWeight="bold"
                      sx={{ mt: 2, display: "block" }}
                    >
                      Dirección Alternativa / Trabajo
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {dataCobro.cliente.clidirec2}
                    </Typography>
                  </>
                )}
              </Paper>

              {/* Tarjeta Garante (Solo si existe) */}
              {dataCobro.cabecera.garcodigo && (
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2, borderTop: "4px solid #ed6c02" }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <PersonPinCircleIcon sx={{ color: "#ed6c02", fontSize: 30, mr: 1 }} />
                    <Typography variant="h6" sx={{ color: "#ed6c02", fontWeight: "bold" }}>
                      Garante Solidario
                    </Typography>
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
                    <Avatar
                      src={
                        dataCobro.garante?.cliimagen_b64
                          ? `data:image/jpeg;base64,${dataCobro.garante.cliimagen_b64}`
                          : ""
                      }
                      sx={{ width: 100, height: 100, mb: 1, border: "2px solid #ed6c02" }}
                    />
                    <Typography variant="body2" fontWeight="bold" align="center">
                      Cód: {dataCobro.cabecera.garcodigo}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="caption" color="textSecondary" fontWeight="bold">
                    Dirección Garante
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {dataCobro.garante?.clidirec || "Dirección no registrada"}
                  </Typography>
                </Paper>
              )}
            </Grid>

            {/* LADO DERECHO: VEHÍCULOS Y FINANZAS */}
            <Grid item xs={12} md={8}>
              {/* Acordeón de Bien Prendado */}
              <Accordion defaultExpanded elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "#f5f5f5" }}>
                  <DirectionsCarIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Bienes Asociados a Recuperar
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {dataCobro.vehiculos?.length > 0 ? (
                    dataCobro.vehiculos.map((v, i) => (
                      <Paper
                        key={i}
                        elevation={0}
                        sx={{ p: 2, mb: 1, bgcolor: "#e1f5fe", border: "1px solid #b3e5fc" }}
                      >
                        <Typography variant="body2" fontWeight="bold" color="primary.dark">
                          {v.mardescri} - {v.artdescri}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 1 }}>
                          {v.descripcion_completa}
                        </Typography>
                      </Paper>
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary" align="center">
                      No se encontraron bienes serializados asociados a esta factura.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Acordeón de Finanzas y Mora */}
              <Accordion defaultExpanded elevation={2} sx={{ borderRadius: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "#fff3e0" }}>
                  <MonetizationOnIcon sx={{ color: "#d32f2f", mr: 1 }} />
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ color: "#d32f2f" }}>
                    Planilla de Cobro / Cuotas Vencidas
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {/* Resumen Financiero Top */}
                  <Grid container spacing={2} mb={3}>
                    <Grid item xs={12} sm={4}>
                      <Paper elevation={0} sx={{ p: 2, textAlign: "center", bgcolor: "#f5f5f5" }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold">
                          Capital Vencido
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {formatMoneda(granCapital)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Paper elevation={0} sx={{ p: 2, textAlign: "center", bgcolor: "#ffebee" }}>
                        <Typography variant="caption" color="error" fontWeight="bold">
                          Interés de Mora
                        </Typography>
                        <Typography variant="h6" color="error" fontWeight="bold">
                          {formatMoneda(granMora)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Paper elevation={0} sx={{ p: 2, textAlign: "center", bgcolor: "#e8f5e9" }}>
                        <Typography variant="caption" color="secondary" fontWeight="bold">
                          GRAN TOTAL
                        </Typography>
                        <Typography variant="h6" color="secondary" fontWeight="bold">
                          {formatMoneda(granTotal)}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: "#eeeeee" }}>
                        <TableRow>
                          <TableCell align="center">
                            <b>Cuota</b>
                          </TableCell>
                          <TableCell align="center">
                            <b>Fec. Vence</b>
                          </TableCell>
                          <TableCell align="center">
                            <b>Días Mora</b>
                          </TableCell>
                          <TableCell align="right">
                            <b>Deuda Cap.</b>
                          </TableCell>
                          <TableCell align="right" sx={{ color: "#d32f2f" }}>
                            <b>Int. Mora</b>
                          </TableCell>
                          <TableCell align="right">
                            <b>Total Pagar</b>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dataCobro.cuotas?.map((c, i) => (
                          <TableRow key={i} hover>
                            <TableCell align="center">{c.facsecuen}</TableCell>
                            <TableCell align="center">{c.facfecven}</TableCell>
                            <TableCell align="center">{c.lDiasVen || 0}</TableCell>
                            <TableCell align="right">{formatMoneda(c.SaldoNeto)}</TableCell>
                            <TableCell align="right" sx={{ color: "#d32f2f", fontWeight: "bold" }}>
                              {formatMoneda(c.Interes)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: "bold" }}>
                              {formatMoneda(c.ValorACobrar)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {(!dataCobro.cuotas || dataCobro.cuotas.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              Esta factura no presenta saldo deudor activo.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </StyledRoot>
      </div>
    </ThemeProvider>
  )
}

export default HojaRutaCobroDetalle
